// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, mergeClasses, ToggleButton, tokens } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular, PersonSupport20Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { DefaultChatUser } from '../../../libs/auth/AuthHelper';
import { useChat } from '../../../libs/hooks/useChat';
import {
    AuthorRoles,
    currentQueueTimeoutIndex,
    IChatMessage,
    SystemNoticeKind,
} from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { Breakpoints } from '../../../styles';
import { formatChatMessage } from '../../utils/ChatMessageUtils';
import { Mic16Regular } from '../../shared/BundledIcons';
import { PromptDialog } from '../prompt-dialog/PromptDialog';
import { QueueTimeoutCallbackOffer } from './QueueTimeoutCallbackOffer';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
import { ChatImagePreview } from './ChatImagePreview';
import { CitationCards } from './CitationCards';
import { RestyleButton } from './RestyleButton';
import { SourcesDropdown } from './SourcesDropdown';
import { UserFeedbackActions } from './UserFeedbackActions';
import Icon from '../../utils/Icon';
import { useTranslation } from 'react-i18next';

// Localization key per system-notice event, keyed by SystemNoticeKind so adding a new kind is a
// compile error until it has an entry here. `withName` flags events whose copy interpolates the
// actor name ({name} = message.userName). Mirrors the agent-side Thread map.
const SYSTEM_NOTICE_KEYS: Record<SystemNoticeKind, { key: string; withName?: boolean }> = {
    AgentJoined: { key: 'chatHistory.system.agentJoined', withName: true },
    ChatResolved: { key: 'chatHistory.system.chatResolved', withName: true },
    ChatTransferred: { key: 'chatHistory.system.chatTransferred', withName: true },
    QueueTimedOut: { key: 'chatHistory.system.queueTimedOut' },
    ChatClosedByCustomer: { key: 'chatHistory.system.chatClosedByCustomer' },
    ScreenshotShared: { key: 'chatHistory.system.screenshotShared' },
    ScreenshotDeclined: { key: 'chatHistory.system.screenshotDeclined' },
};

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderRadius: tokens.borderRadiusMedium,
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
        gap: tokens.spacingHorizontalXS,
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        justifyContent: 'flex-end',
    },
    persona: {
        paddingTop: tokens.spacingVerticalS,
    },
    item: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusXLarge,
        padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalXS}`,
    },
    bot: {
        backgroundColor: tokens.colorSubtleBackground,
        color: tokens.colorNeutralForeground1,
        width: '100%',
        marginLeft: '-6px',
    },
    botEmbedded: {
        backgroundColor: tokens.colorSubtleBackground,
        color: tokens.colorNeutralForeground1,
        width: '100%',
        marginLeft: '2px',
    },
    support: {
        backgroundColor: 'var(--app-support-message-background)',
        color: tokens.colorNeutralForeground1,
        // Mirror the .me bubble's width behaviour (fit-content, capped) but on the
        // left side, so a Support reply visually reads like a chat message rather
        // than a system banner spanning the column. No `embedded` variant — unlike
        // .bot, the support bubble's marginLeft doesn't change between modes.
        width: 'fit-content',
        maxWidth: '100%',
        alignSelf: 'flex-start',
    },
    me: {
        backgroundColor: 'var(--app-user-message-background)',
        color: 'var(--app-user-message-text)',
        width: 'fit-content',
        maxWidth: '100%',
        alignSelf: 'flex-end',
    },
    system: {
        // System broadcast notices ("X joined the chat", "X ended the chat") read as
        // status lines, not chat bubbles: transparent, smaller, muted, and centered so
        // they take up minimal room in the transcript.
        backgroundColor: 'transparent',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        textAlign: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        alignSelf: 'center',
    },
    time: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'absolute',
        right: '0',
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalL,
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: tokens.spacingVerticalS,
    },
    botControls: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: tokens.spacingVerticalS,
        gap: tokens.spacingVerticalXS,
    },
    botControlsRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    citationButton: {
        marginRight: 'auto',
    },
    rlhf: {
        marginLeft: 'auto',
    },
    userImagePreview: {
        maxWidth: '320px',
        maxHeight: '240px',
        borderRadius: '8px',
        marginBottom: tokens.spacingVerticalXS,
        objectFit: 'contain',
        display: 'block',
    },
    content: {
        position: 'relative',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXS}`,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'flex-start',
    },
    botIconWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: tokens.spacingHorizontalS,
        marginLeft: '-6px',
    },
    botIconWrapperEmbedded: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: tokens.spacingHorizontalS,
        marginLeft: '2px',
    },
    botIconLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        fontWeight: 500,
    },
    botIcon: {
        transform: 'rotate(180deg)',
        background: 'var(--app-bot-icon-background)',
        color: tokens.colorNeutralForeground1,
        borderRadius: tokens.borderRadiusCircular,
        boxSizing: 'content-box',
        // padding: `${tokens.spacingHorizontalMNudge} ${tokens.spacingVerticalMNudge}`,
        padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`,
        alignSelf: 'flex-start',
        marginLeft: 0,
        // reduce size of bot icon
        width: '20px',
        height: '20px',
    },
    // Bottom-right of the bubble, where chat apps put a message's meta; currentColor so it reads on both the
    // user's and the bot's bubble colours.
    voiceMarker: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: tokens.spacingVerticalXXS,
        opacity: 0.7,
    },
    supportIcon: {
        background: 'var(--app-support-message-background)',
        color: tokens.colorBrandForegroundLink,
        borderRadius: tokens.borderRadiusCircular,
        boxSizing: 'content-box',
        padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`,
        alignSelf: 'flex-start',
        width: '20px',
        height: '20px',
    },
});

interface ChatHistoryItemProps {
    message: IChatMessage;
    messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, messageIndex }) => {
    const classes = useClasses();
    const chat = useChat();
    const { t } = useTranslation();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);

    const { DynamicReport, EnableDebugCommands, SourcesDropdownReferencesEnabled } = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags,
    );
    const {
        activeUserInfo,
        embeddedMode,
        // features,
    } = useAppSelector((state: RootState) => state.app);
    const [showCitationCards, setShowCitationCards] = useState(false);
    const lastMessageIndex = conversations[selectedId].messages.length - 1;
    const isDefaultUser = message.userName === DefaultChatUser.fullName;
    const isMe =
        isDefaultUser ||
        (message.authorRole === AuthorRoles.User &&
            (message.userId === activeUserInfo?.id || message.userName === activeUserInfo?.username));
    const isBot = message.authorRole === AuthorRoles.Bot;
    const isSupport = message.authorRole === AuthorRoles.Participant;
    const isSystem = message.authorRole === AuthorRoles.System;

    if (isSystem) {
        // Localize the notice from its semantic event, interpolating the agent's name from
        // userName. Unknown/legacy events fall back to the server-provided English content.
        // Returns before the persona/user lookups below — none of them apply to a System notice.
        const notice = message.systemNotice ? SYSTEM_NOTICE_KEYS[message.systemNotice] : undefined;
        const systemText = notice
            ? t(notice.key, notice.withName ? { name: message.userName } : undefined)
            : message.content;
        const offerCallback = currentQueueTimeoutIndex(conversations[selectedId].messages) === messageIndex;
        // System notices skip the persona/bubble chrome entirely — plain text only,
        // no icon row, no citations/feedback controls.
        return (
            <div
                className={classes.root}
                data-testid={`chat-history-item-${messageIndex.toString()}`}
                data-username={message.userName}
                data-content={systemText}
            >
                <div className={classes.content}>
                    <div className={mergeClasses(classes.item, classes.system)}>{systemText}</div>
                    {offerCallback && <QueueTimeoutCallbackOffer />}
                </div>
            </div>
        );
    }

    const user = isDefaultUser
        ? DefaultChatUser
        : chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;
    // Show the agent's self-chosen display name in full, suffixed with "(Support)". The
    // first-name-only truncation from slice 1 (#1581) was dropped once agents began setting
    // their own display name: they choose exactly what the customer sees, so there's no
    // surname-leak to guard against. Source is message.userName (the backend-controlled value
    // AgentReplyService resolves) rather than fullName, which can carry the raw directory record.
    // Defense-in-depth: trim, and strip anything from '@' onward so a backend regression that
    // let an email through can't leak it. Empty / unsafe values fall back to "Support".
    // The `?? ''` is a defensive null-guard against a serialization regression on legacy
    // participant messages where userName could be missing despite the type.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const supportName = (message.userName ?? '').trim().split('@')[0].trim();
    const supportLabel = supportName ? `${supportName} (Support)` : 'Support';

    const isChatResponseComplete = conversations[selectedId].botResponseStatus !== 'generateResponse';
    const content =
        isBot && message.content.length === 0 ? (
            <TypingIndicator />
        ) : (
            <ChatHistoryTextContent
                message={message}
                isLastMessage={lastMessageIndex == messageIndex}
                isChatResponseComplete={isChatResponseComplete}
                isUser={!isBot}
                dynamicReport={DynamicReport}
                useSourcesDropdown={SourcesDropdownReferencesEnabled}
            />
        );
    const showShowRLHFMessage = message.userId.toLowerCase() === 'bot';

    const botIcon = (label: string) => {
        const botLabel = t('app.subTitle');
        return (
            <div className={embeddedMode ? classes.botIconWrapperEmbedded : classes.botIconWrapper}>
                <Icon
                    name="aia"
                    className={classes.botIcon}
                    size={16}
                    title={label || botLabel}
                    aria-label={label || botLabel}
                />
                <span className={classes.botIconLabel}>{botLabel}</span>
            </div>
        );
        //return <Icon name="aia" className={classes.botIcon} />;
    };

    const supportIconBlock = (label: string) => (
        <div className={embeddedMode ? classes.botIconWrapperEmbedded : classes.botIconWrapper}>
            <PersonSupport20Regular className={classes.supportIcon} aria-label={label} />
            <span className={classes.botIconLabel}>{label}</span>
        </div>
    );

    return (
        <div
            className={classes.root}
            // The following data attributes are needed for CI and testing
            data-testid={`chat-history-item-${messageIndex.toString()}`}
            data-username={fullName}
            data-content={utils.formatChatTextContent(formatChatMessage(message))}
        >
            <div className={isMe ? mergeClasses(classes.content, classes.alignEnd) : classes.content}>
                {isBot && botIcon(fullName)}
                {isSupport && supportIconBlock(supportLabel)}
                <div
                    className={
                        isBot
                            ? mergeClasses(classes.item, embeddedMode ? classes.botEmbedded : classes.bot)
                            : isSupport
                              ? mergeClasses(classes.item, classes.support)
                              : mergeClasses(classes.item, classes.me)
                    }
                >
                    <div className={classes.header}>
                        {isBot && EnableDebugCommands && message.content.length > 0 && (
                            <PromptDialog message={message} />
                        )}
                    </div>
                    {!isBot && message.localImagePreview && (
                        <ChatImagePreview
                            src={message.localImagePreview}
                            alt={t(isSupport ? 'chatHistory.agentImagePreviewAlt' : 'chatHistory.userImagePreviewAlt')}
                            thumbnailClassName={classes.userImagePreview}
                        />
                    )}
                    {content}
                    {message.modality === 'voice' && (
                        <div className={classes.voiceMarker}>
                            <span
                                role="img"
                                aria-label={isBot ? t('chatHistory.voiceAnswered') : t('chatHistory.voiceAsked')}
                                title={isBot ? t('chatHistory.voiceAnswered') : t('chatHistory.voiceAsked')}
                            >
                                <Mic16Regular />
                            </span>
                        </div>
                    )}
                    {message.citations && message.citations.length > 0 && (
                        <div className={classes.controls}>
                            <ToggleButton
                                appearance="subtle"
                                checked={showCitationCards}
                                className={classes.citationButton}
                                icon={showCitationCards ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                                iconPosition="after"
                                onClick={() => {
                                    setShowCitationCards(!showCitationCards);
                                }}
                                size="small"
                            >
                                {`${message.citations.length.toString()} ${
                                    message.citations.length === 1 ? 'citation' : 'citations'
                                }`}
                            </ToggleButton>
                        </div>
                    )}
                    {isBot &&
                        (message.contentMetadataArray?.contentMetadata.length ?? 0) > 0 &&
                        (SourcesDropdownReferencesEnabled ? (
                            <div className={classes.botControls}>
                                <div className={classes.botControlsRow}>
                                    <SourcesDropdown
                                        contentMetadata={message.contentMetadataArray?.contentMetadata}
                                        isChatResponseComplete={isChatResponseComplete}
                                        isLastMessage={lastMessageIndex === messageIndex}
                                    />
                                    <RestyleButton message={message} />
                                </div>
                            </div>
                        ) : (
                            <div className={classes.controls}>
                                <RestyleButton message={message} />
                            </div>
                        ))}
                    {showCitationCards && <CitationCards message={message} />}
                    {showShowRLHFMessage && <UserFeedbackActions message={message} />}
                </div>
            </div>
        </div>
    );
};
